import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import AuthLayout from "./AuthLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, InlineAlert, PageLoader, Spinner } from "@/components/common";
import api from "@/lib/api";
import { formatApiError } from "@/lib/formatError";
import { useAuth } from "@/context/AuthContext";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [info, setInfo] = useState(undefined);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", password: "" });
  const [loading, setLoading] = useState(false);
  const { acceptInvite } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) return setInfo(null);
    api.get(`/auth/invitations/${token}`).then((r) => setInfo(r.data)).catch((err) => { setInfo(null); setError(formatApiError(err)); });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await acceptInvite({ token, ...form });
      toast.success(`Welcome to ${info.organization_name}`);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (info === undefined) return <PageLoader />;
  return (
    <AuthLayout
      title={info ? `Join ${info.organization_name}` : "Invitation"}
      subtitle={info ? `${info.inviter_name || "A teammate"} invited you as ${info.role}.` : undefined}
      footer={<Link to="/login" className="link">Already have an account? Sign in</Link>}
    >
      {!info ? (
        <InlineAlert tone="danger" title="This invitation can't be used">{error || "The link is invalid or has expired. Ask your admin for a new invitation."}</InlineAlert>
      ) : info.existing_account ? (
        <InlineAlert tone="info" title="You already have an account">Sign in with {info.email} to continue.</InlineAlert>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <Field label="Email"><Input value={info.email} disabled /></Field>
          <Field label="Your name" required><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label="Create a password" required hint="At least 8 characters."><Input type="password" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></Field>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Button type="submit" className="w-full" disabled={loading}>{loading && <Spinner className="text-white" />} Accept invitation</Button>
        </form>
      )}
    </AuthLayout>
  );
}
